/**
 * Demo-aware api facade (SKE-4166).
 *
 * This module has the exact surface of `api/index.ts` and is what the app
 * imports. Outside demo mode every call is a straight pass-through, so the real
 * reviewer path (`#/review/{slugId}`) behaves identically — the only change is
 * an extra function call.
 *
 * In demo mode:
 *  - the sentinel review key in the URL (`demo`) is swapped for the real
 *    read-only demo link key before any upstream read,
 *  - settings / posts / comments are served from the visitor's local snapshot,
 *  - mutations are handled entirely client-side by `demo/demoApi.ts`. See the
 *    safety invariant documented there — a demo visitor must never be able to
 *    mutate the shared review link.
 */

import {
  demoFetchCalendar,
  demoFetchComment,
  demoFetchPosts,
  demoFetchSetting,
  demoPostAction,
  demoPostComment,
} from 'demo/demoApi'
import { isDemoMode, resolveUpstreamReviewKey } from 'demo/demoMode'

import * as api from './index'
import type { ExternalReviewSettings } from './ExternalReviewSettings'
import type { UpcomingPosts } from './UpcomingPosts'

// Inherit the full api surface so any endpoint added to `./index` later is
// automatically present here (in pass-through form) — a new consumer can't
// accidentally import a non-demo-aware function from this facade. The explicit
// overrides below take precedence over these star re-exports for the endpoints
// the demo intercepts.
export * from './index'

export const fetchSetting = async (
  reviewKey: string
): Promise<ExternalReviewSettings> =>
  isDemoMode()
    ? ((await demoFetchSetting()) as ExternalReviewSettings)
    : api.fetchSetting(reviewKey)

export const fetchPosts = async (reviewKey: string) =>
  isDemoMode()
    ? ((await demoFetchPosts()) as unknown as UpcomingPosts[])
    : api.fetchPosts(reviewKey)

export const fetchComment = async (reviewKey: string, postId: string) =>
  isDemoMode()
    ? demoFetchComment(postId)
    : api.fetchComment(resolveUpstreamReviewKey(reviewKey), postId)

export const fetchCalendar = async (reviewKey: string, date: Date) =>
  isDemoMode()
    ? demoFetchCalendar()
    : api.fetchCalendar(resolveUpstreamReviewKey(reviewKey), date)

/**
 * CLIENT-ONLY IN DEMO MODE. Never reaches
 * `PUT /v2/collaboration/external-review/{reviewKey}/{postId}/status/{statusKey}`.
 */
export const postAction = async (
  reviewKey: string,
  postId: string,
  statusKey: string
) =>
  isDemoMode()
    ? demoPostAction(postId, statusKey)
    : api.postAction(reviewKey, postId, statusKey)

/**
 * CLIENT-ONLY IN DEMO MODE. Never reaches
 * `PATCH /v2/collaboration/external-review/{reviewKey}/{postId}/comments/{commentId}`.
 */
export const postComment = async (
  reviewKey: string,
  postId: string,
  comment: string,
  commentId: string
) =>
  isDemoMode()
    ? demoPostComment(postId, comment, commentId)
    : api.postComment(reviewKey, postId, comment, commentId)

/*
 * Preview / grid reads stay live even in demo mode — they are GETs against the
 * real demo link, which is exactly what gives the demo real product fidelity.
 * They only need the sentinel key swapped for the upstream one.
 */

export const fetchPostPreview = (reviewKey: string, postId: string) =>
  api.fetchPostPreview(resolveUpstreamReviewKey(reviewKey), postId)

export const fetchPostsPreview = (reviewKey: string) =>
  api.fetchPostsPreview(resolveUpstreamReviewKey(reviewKey))

export const fetchPostSkedPreview = (reviewKey: string, postId: string) =>
  api.fetchPostSkedPreview(resolveUpstreamReviewKey(reviewKey), postId)

export const fetchGridPreview = (reviewKey: string, postId: string) =>
  api.fetchGridPreview(resolveUpstreamReviewKey(reviewKey), postId)

export const fetchGridPreviewByAccount = (
  reviewKey: string,
  accountId: string
) =>
  api.fetchGridPreviewByAccount(resolveUpstreamReviewKey(reviewKey), accountId)
